import { useEffect, useRef } from 'react';
import UAParser from 'ua-parser-js';
import {
  COOKIE_OPTIONS,
  INTERVAL_DISTANCE,
  NETWORK_VERSIONS,
  SEARCH_HUMAN_MULTI_URI,
  DEEPSEA_JOB_RETENTION_DAYS,
  DEEPSEA_COOKIE_NAME,
} from '../settings';

const parser = new UAParser();

// Brandom Morelli's post -- Bionary Search in Javascript. A practical Example
// https://codeburst.io/binary-search-in-javascript-a-practical-example-7fda60ce59a1
export function binarySearch(d, t, s, e) {
  const m = Math.floor((s + e) / 2);
  if (t === d[m].x) return m;
  if (e - 1 === s) return Math.abs(d[s].x - t) > Math.abs(d[e].x - t) ? e : s;
  if (t > d[m].x) return binarySearch(d, t, m, e);
  return binarySearch(d, t, s, m);
}

export function getGenomicInterval(
  location,
  intervalDistance = INTERVAL_DISTANCE,
) {
  const startPosition = Math.max(0, location.position - intervalDistance);
  const endPosition = Math.max(0, location.position + intervalDistance);
  return {
    chromosome: location.chromosome,
    startPosition,
    endPosition,
  };
}

// Use binarySearch but locate the minimum or maximum of repeated values
// placement => min/max
export function binarySearchDuplicates(d, t, s, e, placement) {
  if (placement === 'min') {
    let min = binarySearch(d, t, s, e);
    while (min - 1 >= 0) {
      if (d[min].x === d[min - 1].x) min -= 1;
      else break;
    }
    return min;
  }
  let max = binarySearch(d, t, s, e);
  while (max + 1 < d.length) {
    if (d[max].x === d[max + 1].x) max += 1;
    else break;
  }
  return max;
}

// See Dan Abramov's post -- Making setInterval Declarative...
// https://overreacted.io/making-setinterval-declarative-with-react-hooks/
export function useInterval(callback, delay) {
  const savedCallback = useRef();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  });

  // Set up the interval.
  useEffect(() => {
    function tick() {
      if (typeof savedCallback.current === 'function') savedCallback.current();
    }

    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
    return undefined;
  }, [delay]);
}

export function getSlug(title) {
  if (!title) return null;
  let slug = title.replace(/[: ]/g, '-');
  slug = slug.replace(/\+/g, '').toLowerCase();
  return slug;
}

export function isEmptyObject(obj) {
  return (
    Object.getOwnPropertyNames(obj).length === 0 &&
    Object.getOwnPropertySymbols(obj).length === 0 &&
    Object.getPrototypeOf(obj) === Object.prototype
  );
}

export function isSessionStorageEnabled() {
  const testKey = '_localforage_support_test';
  try {
    sessionStorage.setItem(testKey, '1');
    sessionStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
}

export function sortByProperty(obj, property) {
  // convert object into array
  const sortable = [];
  Object.keys(obj).forEach(key => {
    if (property) {
      sortable.push([key, obj[key][property]]);
    } else sortable.push([key, obj[key]]);
    // each item is an array in format [key, value]
  });

  // sort items by value
  sortable.sort((a, b) => b[1] - a[1]); // compare numbers
  return sortable; // array in format [ [ key1, val1 ], [ key2, val2 ], ... ]
}

export async function doMultiGeneSearch(e, context, version = null) {
  const maxURL = 1800; // GET can only handle url length < ~2000 char
  let resp = null;

  const lines = e.trim().split(/[\t\s,\n]+/);
  const qq = [];

  for (let i = 0; i < lines.length; i += 1) {
    let line = lines[i];
    line = line.trim();
    line = line.split('#')[0];
    line = line.replace(/\++/g, '+');

    // Ideally we would also trim any beginning and ending '+' characters but
    // JS doesn't have this function. DRF search endpoint deals with such
    // superfluous delimiters correctly anyway.
    if (line) {
      qq.push(line);
    }
  }

  if (qq.length) {
    let q = qq.join('+');

    // Useful if any line begins or ends with '+' so we end up with duplicate
    // +'s after join.
    q = q.replace(/\++/g, '+');

    // Add version parameter if provided (for filtering genes by compendium)
    const versionParam = version ? `&version=${version}` : '';

    if (`${SEARCH_HUMAN_MULTI_URI}${q}${versionParam}`.length < maxURL) {
      const url = `${SEARCH_HUMAN_MULTI_URI}${q}${versionParam}`;
      resp = await context.fetch(url);
    } else {
      // POST request for large queries - preserve organism and version in query string
      const baseUrl = SEARCH_HUMAN_MULTI_URI.replace(/&q=$/, '').replace(
        /\?q=$/,
        '?',
      );
      const url = version ? `${baseUrl}&version=${version}` : baseUrl;
      resp = await context.fetch(url, {
        method: 'POST',
        body: `{"q": "${q}"}`,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    const json = await resp.json();
    return json;
  }
  return [];
}

export function isObject(n) {
  return Object.prototype.toString.call(n) === '[object Object]';
}

export function hasReservedUrlChars(text) {
  return /[{}~[\]/|\\?=^&%`"'#]+/g.test(text);
}

export function isSupportedUserAgent() {
  const ua = parser.getResult();
  return !(
    ua.browser.name === 'IE' ||
    ua.browser.name === 'IEMobile' ||
    (ua.browser.name === 'Edge' && ua.engine.name === 'EdgeHTML')
  );
}

export function isExPecto(score) {
  return score && score.short_name === 'expecto';
}

// Adapted from: https://www.codetinkerer.com/2019/01/14/cancel-async-requests-pattern.html
if (!global.AbortController) global.AbortController = Object;

export function createCancelOnReentry() {
  // Each runner owns one request stream, so unrelated call sites do not
  // cancel one another.
  let abortController = new AbortController();

  return async request => {
    abortController.abort();
    abortController = new AbortController();

    try {
      return await request(abortController.signal);
    } catch (error) {
      if (error.name === 'AbortError') return undefined;
      throw error;
    }
  };
}

const cancelVariantFetchOnReentry = createCancelOnReentry();

export async function cancelFetchOnReentry(fetch, url) {
  return cancelVariantFetchOnReentry(signal => fetch(url, { signal }));
}

// Takes an object and an array of keys to remove from the object,
//  returns a new object without the submitted keys
export const omitKeys = (obj, arr) =>
  Object.keys(obj)
    .filter(k => !arr.includes(k))
    // eslint-disable-next-line no-sequences
    .reduce((acc, key) => ((acc[key] = obj[key]), acc), {});

// //////////////////////////
// Cookie utils
// We needed something that was simple and executable outside of react
export function setCookie(name, value) {
  document.cookie = `${name}=${JSON.stringify(value)}; max-age=${
    COOKIE_OPTIONS.maxAge
  };path=${COOKIE_OPTIONS.path}`;
}

// End cookie utils
// //////////////////////////////////////////////////////

export function getHttps(url) {
  return url.replace(/^http:\/\//i, 'https://');
}

export function getGeneListTSV(geneList) {
  let content = `SYMBOL\tENTREZ\tDESCRIPTION\tALIASES\tXREFS\n`;
  geneList.forEach(gene => {
    content += `${gene.standard_name}\t${gene.entrez}\t${gene.description}\t${gene.aliases}\t${gene.xrefs}\n`;
  });
  return content;
}

export function getUrlFromActivity(activityItem) {
  return activityItem.customUrl !== undefined
    ? `/${activityItem.customUrl}`
    : `/${activityItem.value}`;
}

export const scientificNotation = num => {
  if (num >= 1 && num < 1000) {
    return num.toFixed(2);
  }
  let exponent = Math.floor(Math.log10(Math.abs(num)));
  let mantissa = num / 10 ** exponent;

  while (Math.abs(mantissa) >= 10) {
    mantissa /= 10;
    exponent += 1;
  }

  // Split mantissa into integer and fractional parts
  const [integer, fractional] = mantissa.toString().split('.');

  // Round the fractional part to two digits
  const fractionalPart = (fractional ? `.${fractional}` : '').slice(0, 3);
  mantissa = integer + fractionalPart;

  return `${mantissa}e${exponent}`;
};

export const isUsingDevToolsFromContext = (context, cookieName) => {
  const { isUsingDevTools } = context;
  return typeof isUsingDevTools === 'function'
    ? isUsingDevTools(cookieName)
    : false;
};

export const areEntrezListsEqual = (listA, listB) => {
  const normalizedListA = listA
    .map(item => parseInt(item, 10))
    .sort((a, b) => a - b);
  const normalizedListB = listB
    .map(item => parseInt(item, 10))
    .sort((a, b) => a - b);

  return JSON.stringify(normalizedListA) === JSON.stringify(normalizedListB);
};

// DeepSEA Job Storage Utilities
const DEEPSEA_JOB_PREFIX = 'deepsea_job_';
const MAX_JOB_AGE_MS = DEEPSEA_JOB_RETENTION_DAYS * 24 * 60 * 60 * 1000; // Convert days to milliseconds

/**
 * Save a DeepSEA job to localStorage
 */
export function saveJobToLocalStorage(jobId, jobData) {
  try {
    localStorage.setItem(
      `${DEEPSEA_JOB_PREFIX}${jobId}`,
      JSON.stringify(jobData),
    );
    return true;
  } catch (e) {
    console.warn(`Failed to save job ${jobId} to localStorage:`, e);
    return false;
  }
}

/**
 * Load a DeepSEA job from localStorage
 */
export function loadJobFromLocalStorage(jobId) {
  try {
    const data = localStorage.getItem(`${DEEPSEA_JOB_PREFIX}${jobId}`);
    return data ? JSON.parse(data) : null;
  } catch (e) {
    console.warn(`Failed to load job ${jobId} from localStorage:`, e);
    return null;
  }
}

/**
 * Remove a DeepSEA job from localStorage
 */
export function removeJobFromLocalStorage(jobId) {
  try {
    localStorage.removeItem(`${DEEPSEA_JOB_PREFIX}${jobId}`);
    return true;
  } catch (e) {
    console.warn(`Failed to remove job ${jobId} from localStorage:`, e);
    return false;
  }
}

/** @returns {string[] | null} */
export function loadDeepSEAJobIds() {
  try {
    const stored = localStorage.getItem(DEEPSEA_COOKIE_NAME);
    const jobIds = stored === null ? null : JSON.parse(stored)?.jobIds;
    // Only usable ID lists can hydrate jobs; malformed entries allow migration.
    return Array.isArray(jobIds) && jobIds.every(id => typeof id === 'string')
      ? jobIds
      : null;
  } catch {
    // Unavailable storage stays empty rather than triggering migration writes.
    return [];
  }
}

export function saveDeepSEAJobIds(jobIds) {
  try {
    // No count limit: records age out after DEEPSEA_JOB_RETENTION_DAYS.
    localStorage.setItem(DEEPSEA_COOKIE_NAME, JSON.stringify({ jobIds }));
    return true;
  } catch {
    return false;
  }
}

/**
 * Clean up old DeepSEA jobs from localStorage
 */
export function cleanupOldDeepSEAJobs() {
  const now = Date.now();
  const validJobIds = [];

  try {
    // Iterate through all localStorage keys
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(DEEPSEA_JOB_PREFIX)) {
        const jobId = key.replace(DEEPSEA_JOB_PREFIX, '');
        try {
          const jobData = JSON.parse(localStorage.getItem(key));
          const created = new Date(jobData.created).getTime();

          if (now - created > MAX_JOB_AGE_MS) {
            // Mark for removal
            keysToRemove.push(key);
          } else {
            validJobIds.push(jobId);
          }
        } catch (e) {
          // Remove corrupted entries
          keysToRemove.push(key);
        }
      }
    }

    // Remove old/corrupted jobs
    keysToRemove.forEach(key => localStorage.removeItem(key));

    return validJobIds;
  } catch (e) {
    console.warn('Failed to cleanup old DeepSEA jobs:', e);
    return validJobIds;
  }
}

/**
 * Find the best default integration from GIANT relevant networks.
 * For MAGE: maps top 2 GIANT relevant slugs to -v3 equivalents.
 * For GIANT: uses top relevant network directly.
 * Falls back to global-v3 (MAGE) or global (GIANT).
 */
/**
 * Get the global integration slug for a given version.
 * v1 → 'global', v3 → 'global-v3'
 */
export function globalSlugFor(version) {
  return version === NETWORK_VERSIONS.GIANT ? 'global' : `global-${version}`;
}

/**
 * Extract the version-agnostic base tissue name from a slug.
 * 'kidney-v3' → 'kidney', 'kidney' → 'kidney', 'global-v3' → 'global'
 */
export function baseSlug(slug) {
  return slug ? slug.replace(/-v3$/, '') : null;
}

/**
 * Resolve a version-agnostic tissue name to a versioned integration slug.
 * Checks the actual integrations list so non-overlapping tissues fall back
 * to null (caller should fall back to global).
 * e.g., ('kidney', 'v3', integrations) → 'kidney-v3'
 *       ('kidney', 'v1', integrations) → 'kidney'
 *       ('brainstem', 'v1', integrations) → null (v3-only tissue)
 */
export function resolveSlug(tissue, giantVersion, integrations) {
  const candidates =
    giantVersion === NETWORK_VERSIONS.MAGE
      ? [`${tissue}-v3`, tissue]
      : [tissue, `${tissue}-v3`];
  for (let i = 0; i < candidates.length; i += 1) {
    if (integrations.find(item => item.slug === candidates[i])) {
      return candidates[i];
    }
  }
  return null;
}

/**
 * The text of a picked file, read in the browser; nothing is uploaded.
 * @param {File} file
 * @returns {Promise<string>}
 */
export function readFileText(file) {
  return new Promise(function read(resolve, reject) {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}
